import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Input, Pagination, Divider, Spin } from 'antd';
import { WithLatestStateDeviceNode } from '../RootTree';
import { LoadingOutlined } from '@ant-design/icons';
import { useListAllDevicesByPage } from '../../../../hooks/socket/cameras';
import * as SERVICE from '../../../../constants/service.types';
import { useDispatch, useSelector } from 'react-redux';
import { socketEmitEvent } from '../../../../redux/actions';
import { updateRecoveryCameraPage } from '../../../../redux/recovery/action';
import { getRecoveryCameraPage } from '../../../../redux/recovery/reducer';

const AllCamerasV1 = ({
  cameras,
  currentPage,
  pageSize,
  onPageChange,
  isLoading,
  total,
  getIsCameraPinned,
  onPinCamera,
  selectedSerial,
  onSelectCamera,
  namespace,
  allowRemove,
  allowEdit,
  onRemoveCamera,
  onUpdateCamera,
  onUpdateStream,
}) => {
  const timeRequestRef = useRef(Date.now());
  const {
    isLoading: isSearching,
    data: searchResult,
    send,
  } = useListAllDevicesByPage();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const searchNameRef = useRef('');
  const dispatch = useDispatch();
  const { searchCameraKeyword: recoverySearchCameraKeyword } = useSelector(
    getRecoveryCameraPage,
  );
  const [search, setSearch] = useState('');

  const fetchCameras = useCallback(
    ({ page, name }) => {
      let data1 = {
        namespace,
        deviceType: 'camera',
        timeRequest: timeRequestRef.current,
        page,
        size: 15,
        tag: 'live-list',
        name,
      };
      send(data1);
    },
    [namespace, send],
  );

  const handleSearch = (value) => {
    if (!value) {
      setIsSearchActive(false);
      setSearch('');
      dispatch(
        updateRecoveryCameraPage({
          searchCameraKeyword: '',
        }),
      );
      return;
    }

    setSearch(value);
    dispatch(
      updateRecoveryCameraPage({
        searchCameraKeyword: value,
      }),
    );

    setIsSearchActive(true);
    timeRequestRef.current = Date.now();
    fetchCameras({ page: 0, name: value });
  };

  useEffect(() => {
    if (recoverySearchCameraKeyword) {
      setSearch(recoverySearchCameraKeyword);
      setIsSearchActive(true);
      timeRequestRef.current = Date.now();
      fetchCameras({ page: 0, name: recoverySearchCameraKeyword });
    }
  }, [recoverySearchCameraKeyword, fetchCameras]);

  const finalCurrentPage = useMemo(() => {
    if (!isSearchActive) {
      return currentPage;
    }

    return searchPage;
  }, [isSearchActive, searchPage, currentPage]);

  const finalTotal = useMemo(() => {
    if (!isSearchActive) {
      return total;
    }

    return searchResult?.total || 0;
  }, [isSearchActive, searchResult, total]);

  const handlePageChange = useCallback(
    (page, pageSize) => {
      if (!isSearchActive) {
        onPageChange(page, pageSize);
      } else {
        setSearchPage(page);
        fetchCameras({ page: page - 1, name: search });
      }
    },
    [isSearchActive, onPageChange, fetchCameras, search],
  );

  const finalPageSize = useMemo(() => {
    if (!isSearchActive) {
      return pageSize;
    }

    return 15;
  }, [isSearchActive, pageSize]);

  const camerasToRender = useMemo(() => {
    if (!isSearchActive) {
      return cameras;
    }

    return searchResult?.devices || [];
  }, [isSearchActive, searchResult, cameras]);

  return (
    <>
      <Input.Search
        placeholder="Search camera"
        onSearch={handleSearch}
        loading={isSearching}
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <Divider className="camera-list-divider" />
      <div className="camera-list">
        {(isLoading || isSearching) && (
          <div className="camera-list-loading">
            <Spin indicator={<LoadingOutlined />} />
          </div>
        )}
        <div className="camera-list-items">
          {!isLoading && !isSearching && (
            <React.Fragment>
              {camerasToRender?.map((cam) => (
                <WithLatestStateDeviceNode
                  key={cam.serial}
                  name={cam.name}
                  serial={cam.serial}
                  state={cam.state}
                  pinned={getIsCameraPinned?.(cam.serial) || false}
                  onPin={(pinned) => onPinCamera(cam, pinned)}
                  isSelected={selectedSerial === cam.serial}
                  onSelect={() => onSelectCamera(cam)}
                  allowRemove={allowRemove}
                  camera={allowEdit ? cam : undefined}
                  variant="all"
                  onUpdate={onUpdateCamera}
                  onUpdateStream={onUpdateStream}
                  onRemove={() => onRemoveCamera(cam.serial)}
                />
              ))}
            </React.Fragment>
          )}
        </div>

        <div className="camera-list-pagination">
          <Pagination
            size="small"
            showLessItems
            showSizeChanger={false}
            total={finalTotal}
            current={finalCurrentPage}
            pageSize={finalPageSize}
            onChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
};

export const AllCamerasRootGroup = ({
  getIsCameraPinned,
  onPinCamera,
  selectedSerial,
  onSelectCamera,
  namespace,
  onUpdateCamera,
  onUpdateStream,
  updateReturn,
}) => {
  const { data, isLoading, send, patch } = useListAllDevicesByPage();
  const timeRequestRef = useRef(Date.now());
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
  });
  const dispatch = useDispatch();
  const { pagination: recoveryPagination } = useSelector(getRecoveryCameraPage);

  const onRemoveCamera = (serial) => {
    console.log('onRemoveCamera =>>>', serial);
    let data = {
      serial,
      namespace,
    };
    let dataObj = {
      appServiceType: SERVICE.REMOVE_DEVICE,
      requestId: new Date().getTime(),
      body: JSON.stringify(data),
    };

    dispatch(socketEmitEvent(dataObj));
    setTimeout(() => {
      send({
        namespace,
        tag: 'camera-list',
        deviceType: 'camera',
        page: pagination.current - 1,
        size: pagination.pageSize,
        timeRequest: timeRequestRef.current,
      });
    }, 10);
  };

  useEffect(() => {
    send({
      namespace,
      tag: 'camera-list',
      deviceType: 'camera',
      page: pagination.current - 1,
      size: pagination.pageSize,
      timeRequest: timeRequestRef.current,
    });
  }, [send, namespace, timeRequestRef, pagination]);

  useEffect(() => {
    if (updateReturn && updateReturn.isSuccess) {
      patch((prev) => {
        if (!prev) {
          return prev;
        }

        if (!prev.devices.find((d) => d.serial === updateReturn.data.serial)) {
          return prev;
        }

        return {
          ...prev,
          devices: prev.devices.map((d) =>
            d.serial === updateReturn.data.serial
              ? {
                  ...d,
                  ...updateReturn.data,
                }
              : d,
          ),
        };
      });
    }
  }, [updateReturn, patch]);

  useEffect(() => {
    if (recoveryPagination) {
      setPagination({
        current: recoveryPagination.page,
        pageSize: recoveryPagination.size,
      });
    }
  }, [recoveryPagination]);

  return (
    <AllCamerasV1
      cameras={data?.devices || []}
      currentPage={pagination.current}
      pageSize={pagination.pageSize}
      onPageChange={(page) => {
        setPagination({ ...pagination, current: page });
        dispatch(
          updateRecoveryCameraPage({
            pagination: {
              page: page,
              size: pagination.pageSize,
            },
          }),
        );
      }}
      isLoading={isLoading}
      total={data?.total || 0}
      getIsCameraPinned={getIsCameraPinned}
      onPinCamera={onPinCamera}
      selectedSerial={selectedSerial}
      onSelectCamera={onSelectCamera}
      namespace={namespace}
      allowEdit
      onUpdateCamera={onUpdateCamera}
      onUpdateStream={onUpdateStream}
      onRemoveCamera={onRemoveCamera}
    />
  );
};

export const AllCamerasPinnedGroup = ({
  getIsCameraPinned,
  onPinCamera,
  selectedSerial,
  onSelectCamera,
  namespace,
  cameras,
  onUpdateCamera,
  onUpdateStream,
}) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
  });
  const dispatch = useDispatch();
  const { pagination: recoveryPagination } = useSelector(getRecoveryCameraPage);

  const camerasToRender = useMemo(() => {
    return cameras.slice(
      (pagination.current - 1) * pagination.pageSize,
      pagination.current * pagination.pageSize,
    );
  }, [cameras, pagination]);

  useEffect(() => {
    if (recoveryPagination) {
      setPagination({
        current: recoveryPagination.page,
        pageSize: recoveryPagination.size,
      });
    }
  }, [recoveryPagination]);

  return (
    <AllCamerasV1
      cameras={camerasToRender}
      currentPage={pagination.current}
      pageSize={pagination.pageSize}
      onPageChange={(page) => {
        setPagination({ ...pagination, current: page });
        dispatch(
          updateRecoveryCameraPage({
            pagination: {
              page,
              size: pagination.pageSize,
            },
          }),
        );
      }}
      isLoading={false}
      total={cameras.length}
      getIsCameraPinned={getIsCameraPinned}
      onPinCamera={onPinCamera}
      selectedSerial={selectedSerial}
      onSelectCamera={onSelectCamera}
      namespace={namespace}
      allowEdit
      onUpdateCamera={onUpdateCamera}
      onUpdateStream={onUpdateStream}
      onRemoveCamera={console.log}
    />
  );
};
